import { useCallback, useMemo } from 'react';
import { FieldMappingEntry } from '../../../../../../types/DataSyncViewModel';
import { MappingMode } from '../../../../../../types/DataSyncItem';

export interface UseFieldMappingsHandlersParams {
  viewModelFieldMappings: FieldMappingEntry[];
  fieldMappingMode: MappingMode;
  editingDestinationPath: string | undefined;
  onChange: (updatedFieldMappings: FieldMappingEntry[]) => void;
  closeDialog: () => void;
}

export interface UseFieldMappingsHandlersReturn {
  handleSelectSourceField: (sourcePath: string) => void;
  handleRemoveMapping: (destinationPath: string) => void;
  handleToggleDisabled: (destinationPath: string) => void;
  currentSourceField: string | undefined;
}

/**
 * Custom hook for managing TreeFieldMappingsTable event handlers
 * Handles mapping operations (select, remove, toggle)
 */
export const useFieldMappingsHandlers = ({
  viewModelFieldMappings,
  fieldMappingMode,
  editingDestinationPath,
  onChange,
  closeDialog,
}: UseFieldMappingsHandlersParams): UseFieldMappingsHandlersReturn => {
  // Handler when a source field is selected
  const handleSelectSourceField = useCallback(
    (sourcePath: string) => {
      if (!editingDestinationPath) {
        return;
      }

      // Update the mapping for the selected destination field
      const updatedMappings = viewModelFieldMappings.filter(
        (m) => m.destinationField !== editingDestinationPath
      );

      if (fieldMappingMode === MappingMode.FIXED) {
        const existingMappingForSource = viewModelFieldMappings.find(
          (m) =>
            m.sourceField === sourcePath &&
            m.destinationField !== editingDestinationPath
        );

        if (existingMappingForSource) {
          existingMappingForSource.disabled = true;
        }
      }

      updatedMappings.push({
        destinationField: editingDestinationPath,
        sourceField: sourcePath,
        disabled: false,
      });

      onChange(updatedMappings);
      closeDialog();
    },
    [
      editingDestinationPath,
      viewModelFieldMappings,
      fieldMappingMode,
      onChange,
      closeDialog,
    ]
  );

  // Get currently selected source field for the editing destination
  const currentSourceField = useMemo(() => {
    if (!editingDestinationPath) {
      return undefined;
    }
    const mapping = viewModelFieldMappings.find(
      (m) => m.destinationField === editingDestinationPath
    );
    return mapping?.sourceField;
  }, [editingDestinationPath, viewModelFieldMappings]);

  // Handler to remove a mapping
  const handleRemoveMapping = useCallback(
    (destinationPath: string) => {
      const updatedMappings = viewModelFieldMappings.filter(
        (m) => m.destinationField !== destinationPath
      );
      onChange(updatedMappings);
    },
    [viewModelFieldMappings, onChange]
  );

  // Handler to toggle the disabled state of a mapping
  const handleToggleDisabled = useCallback(
    (destinationPath: string) => {
      const updatedMappings = viewModelFieldMappings.map((mapping) => {
        if (mapping.destinationField === destinationPath) {
          return {
            ...mapping,
            disabled: !mapping.disabled,
          };
        }
        return mapping;
      });

      onChange(updatedMappings);
    },
    [viewModelFieldMappings, onChange]
  );

  return {
    handleSelectSourceField,
    handleRemoveMapping,
    handleToggleDisabled,
    currentSourceField,
  };
};
